import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../../../../store/store";
import {
  fetchDeletedRepairsAsync,
  restoreRepairAsync,
  permanentDeleteRepairAsync,
  clearMessages,
} from "../../../../../store/Slice/recycleBinRepairSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const RepairRecycleBin: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { items = [], total = 0, status, error, message } = useSelector(
    (s: RootState) => s.recycleRepair
  );
  const { user } = useSelector((s: RootState) => s.auth);

  const [page, setPage] = useState(1);
  const limit = 20;

  // -------------------------------
  // SELECT STATE
  // -------------------------------
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const isAllSelected = selectedIds.length === items.length && items.length > 0;

  useEffect(() => {
    dispatch(fetchDeletedRepairsAsync({ page, limit }));
  }, [dispatch, page]);

  useEffect(() => {
    if (user && user.userType && !["admin", "subadmin"].includes(user.userType)) {
      navigate("/user/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (message) {
      toast.success(String(message));
      setSelectedIds([]); // Clear selection after actions
      dispatch(clearMessages());
      dispatch(fetchDeletedRepairsAsync({ page, limit }));
    }
    if (error) {
      toast.error(String(error));
      dispatch(clearMessages());
    }
  }, [message, error, dispatch, page]);

  // -------------------------------
  // SELECT ONE
  // -------------------------------
  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  // -------------------------------
  // SELECT ALL
  // -------------------------------
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((i) => i._id));
    }
  };

  // -------------------------------
  // Restore / Delete Single
  // -------------------------------
  const handleRestore = async (id: string) => {
    if (!user || !["admin", "subadmin"].includes(user.userType)) {
      toast.error("You do not have permission to restore items");
      return;
    }
    if (!confirm("Restore this repair?")) return;
    await dispatch(restoreRepairAsync(id));
  };

  const handlePermanentDelete = async (id: string) => {
    if (!user || !["admin", "subadmin"].includes(user.userType)) {
      toast.error("You do not have permission to delete items");
      return;
    }
    if (!confirm("Permanently delete this repair? This cannot be undone.")) return;
    await dispatch(permanentDeleteRepairAsync(id));
    dispatch(fetchDeletedRepairsAsync({ page, limit }));
  };

  // -------------------------------
  // BULK ACTIONS
  // -------------------------------
  const handleBulkRestore = () => {
    if (!confirm(`Restore ${selectedIds.length} items?`)) return;
    selectedIds.forEach((id) => dispatch(restoreRepairAsync(id)));
  };

  const handleBulkDelete = () => {
    if (
      !confirm(
        `Permanently delete ${selectedIds.length} items? This cannot be undone.`
      )
    )
      return;
    selectedIds.forEach((id) => dispatch(permanentDeleteRepairAsync(id)));
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Repair Recycle Bin</h2>
      </div>

      {/* BULK ACTION BUTTONS */}
      {selectedIds.length > 0 && (
        <div className="flex gap-3 mb-4">
          <button
            onClick={handleBulkRestore}
            className="px-4 py-2 bg-green-600 text-white rounded"
          >
            Restore Selected ({selectedIds.length})
          </button>

          <button
            onClick={handleBulkDelete}
            className="px-4 py-2 bg-red-600 text-white rounded"
          >
            Delete Selected ({selectedIds.length})
          </button>
        </div>
      )}

      {status === "loading" && <div>Loading...</div>}

      {items.length === 0 ? (
        <div className="p-4 bg-white rounded shadow">No items in recycle bin.</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded shadow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-5 text-center"> 
                  <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} /> <span>ALL SELECT</span>
                </th>
                <th className="p-2 text-center">Order</th>
                <th className="p-2 text-center">Type</th>
                <th className="p-2 text-center">Company</th>
                <th className="p-2 text-center">Deleted At</th>
                <th className="p-2 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {items.map((r) => (
                <tr key={r._id} className="border-t">
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(r._id)}
                      onChange={() => toggleSelectOne(r._id)}
                    />
                  </td>

                  <td className="p-2">{r.orderNumber}</td>
                  <td className="p-2">{r.requestType}</td>
                  <td className="p-2">{r.companyName}</td>
                  <td className="p-2">
                    {r.deletedAt ? new Date(r.deletedAt).toLocaleString() : "-"}
                  </td>

                  <td className="p-2 flex flex-wrap gap-2 justify-center">
                    {["admin", "subadmin"].includes(user?.userType) ? (
                      <>
                        <button
                          onClick={() => handleRestore(r._id)}
                          className="px-2 py-1 bg-green-500 text-white rounded"
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(r._id)}
                          className="px-2 py-1 bg-red-600 text-white rounded"
                        >
                          Delete Forever
                        </button>
                      </>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No actions available
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > limit && (
        <div className="mt-4 flex gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 bg-gray-200 rounded"
          >
            Prev
          </button>
          <div className="px-3 py-1">Page {page}</div>
          <button
            disabled={page * limit >= total}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 bg-gray-200 rounded"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default RepairRecycleBin;
